/**
 * BUG-042 — shared friendly-message mapping for POST /pieces/:id/run
 * failures. Extracted from PieceView's RunPipelineRow (FEAT-009 / S-106) so
 * DecisionBar's "Next Step" flow can surface the same known-error messages
 * (missing BYOAPI key, manual-mode workspace, pg-boss unavailable, etc.)
 * instead of silently swallowing them.
 */
import { ApiError } from "@/lib/api-error";

export function describeRunError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 503) {
      return "Pipeline queue unavailable — operator action needed (BUG: pg-boss permission).";
    }
    if (err.status === 403) {
      return "You don't have permission to run the pipeline.";
    }
    if (err.status === 409) {
      // FEAT-009 / S-106 — differentiate the known 409 shapes.
      // err.body is the raw response text from the api layer.
      const body = err.body ?? "";
      if (body.includes("llm_access_not_set_up")) {
        // FEAT-106 / S-424 — a regular tenant: the platform does not provide
        // LLM access yet. The founder's wording, kept identical to the API's.
        return "LLM access is not set up yet, contact Nagareru";
      }
      if (body.includes("preferred_provider_no_key")) {
        // FEAT-013 — publication/workspace prefers a provider with no key.
        return "This publication is set to use an LLM provider that has no key configured. Add the key in Settings → API Keys, or change the provider in the publication settings.";
      }
      if (body.includes("no_byoapi_key")) {
        return "No API key configured for this workspace. Add an Anthropic / Gemini / OpenAI key in Settings → API Keys before running the pipeline.";
      }
      if (body.includes("run_mode_manual")) {
        return "This workspace runs pipelines from the operator's CLI. Contact the operator to trigger this piece.";
      }
      // FEAT-108 / S-438 — POST /pieces/:id/decision's own refusals carry a
      // message written for the person who clicked.
      if (body.includes("no_decision") || body.includes("already_published")) {
        return apiMessage(body) ?? "This piece has already moved on — reload it.";
      }
      return "A pipeline run is already in progress for this piece.";
    }
    return err.message || "Could not start the pipeline.";
  }
  return err instanceof Error ? err.message : "Could not start the pipeline.";
}

function apiMessage(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    return typeof parsed.message === "string" ? parsed.message : null;
  } catch {
    return null;
  }
}

/**
 * FEAT-108 / S-438 — did POST /pieces/:id/decision record the decision even
 * though it answered an error? It does when the decision landed and only the
 * run that follows was refused (no key, queue down, run already going): the
 * response then carries `decision`. The revision modal closes in that case —
 * leaving it open invites a second submit of a revision already recorded.
 */
export function decisionLanded(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  try {
    const parsed = JSON.parse(err.body) as { decision?: unknown };
    return typeof parsed.decision === "string";
  } catch {
    return false;
  }
}

/**
 * FEAT-069 / S-335 — friendly translation for `pieces.run_error`, the raw
 * exception message a mid-run pipeline failure leaves behind (set by
 * runner.ts's catch block as `err.message`). Unlike describeRunError()
 * above (trigger-time HTTP errors from POST /pieces/:id/run), this maps
 * the shapes thrown by the pipeline skills themselves — most commonly the
 * fail-loud parse errors from BUG-007/BUG-049/FEAT-069 (S-332/S-333) and
 * the LeakedJsonContentError safety net (S-334). The raw message stays
 * available to the caller for a details toggle — this only supplies the
 * plain-language headline.
 */
export function describeRunErrorMessage(raw: string): string {
  if (/\b(401|403|invalid api key|unauthorized|permission)\b/i.test(raw)) {
    return "The AI provider rejected the request — the API key may be revoked or invalid. Re-test it in Settings → API Keys, then click Retry.";
  }
  if (/unparseable output|could not extract|no parseable/i.test(raw)) {
    return "The AI returned a response that couldn't be understood as valid content. This is usually a one-off — click Retry to try again.";
  }
  if (/was missing|no curated items|no usable/i.test(raw)) {
    return "The AI's response was missing required content. This is usually a one-off — click Retry to try again.";
  }
  if (/unparsed LLM JSON wrapper/i.test(raw)) {
    return "A safety check caught malformed generated content before it was saved. This is usually a one-off — click Retry to try again.";
  }
  return "The pipeline run failed. The technical details are available below.";
}
